/**
 * Create/Edit Folder Dialog
 * 
 * Folders are collections without schema - purely for organization in the UI.
 * Reference: reference/app/src/modules/settings/routes/data-model/collections/components/collection-dialog.vue
 */

import { useState, useEffect } from 'react';
import { FolderPlus } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCreateCollection, useUpdateCollection } from '@/hooks/useCollections';
import type { Collection } from '@/api/collections';
import { toast } from 'sonner';

interface CreateFolderDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    folder?: Collection | null;
    onSuccess?: () => void;
}

export function CreateFolderDialog({ open, onOpenChange, folder, onSuccess }: CreateFolderDialogProps) {
    const [folderKey, setFolderKey] = useState('');
    const [icon, setIcon] = useState('folder');
    const [color, setColor] = useState('#6366f1');
    const [note, setNote] = useState('');

    const createCollection = useCreateCollection();
    const updateCollection = useUpdateCollection();

    const isEdit = !!folder;

    // Initialize form when dialog opens or folder changes
    useEffect(() => {
        if (open) {
            if (folder) {
                setFolderKey(folder.collection);
                setIcon(folder.meta?.icon || 'folder');
                setColor(folder.meta?.color || '#6366f1');
                setNote(folder.meta?.note || '');
            } else {
                setFolderKey('');
                setIcon('folder');
                setColor('#6366f1');
                setNote('');
            }
        }
    }, [open, folder]);

    const handleSave = async () => {
        if (!folderKey.trim()) {
            toast.error('Folder key is required');
            return;
        }

        // Validate folder key format (lowercase, underscores, no spaces)
        const validKey = /^[a-z_][a-z0-9_]*$/.test(folderKey);
        if (!validKey && !isEdit) {
            toast.error('Folder key must start with a letter or underscore and contain only lowercase letters, numbers, and underscores');
            return;
        }

        try {
            if (isEdit) {
                // Update existing folder
                await updateCollection.mutateAsync({
                    collection: folderKey,
                    payload: {
                        icon,
                        color,
                        note: note || null,
                    },
                });
                toast.success('Folder updated successfully');
            } else {
                // Create new folder (collection without schema)
                await createCollection.mutateAsync({
                    collection: folderKey,
                    meta: {
                        icon,
                        color,
                        note: note || null,
                        hidden: false,
                        singleton: false,
                    },
                    schema: null, // No schema = folder
                });
                toast.success('Folder created successfully');
            }

            onOpenChange(false);
            onSuccess?.();
        } catch (error: any) {
            const message = error?.response?.data?.errors?.[0]?.message || error.message || 'Failed to save folder';
            toast.error(message);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FolderPlus className="h-5 w-5" />
                        {isEdit ? 'Edit Folder' : 'Create Folder'}
                    </DialogTitle>
                    <DialogDescription>
                        {isEdit
                            ? 'Update folder metadata.'
                            : 'Folders help organize collections in the navigation sidebar.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 py-4">
                    {/* Folder Key */}
                    <div className="grid gap-2">
                        <Label htmlFor="folder-key">
                            Folder Key <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="folder-key"
                            value={folderKey}
                            onChange={(e) => setFolderKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                            placeholder="my_folder"
                            disabled={isEdit}
                            autoFocus
                        />
                        <p className="text-xs text-muted-foreground">
                            Lowercase letters, numbers, and underscores only. Cannot be changed after creation.
                        </p>
                    </div>

                    {/* Icon */}
                    <div className="grid gap-2">
                        <Label htmlFor="folder-icon">Icon</Label>
                        <Input
                            id="folder-icon"
                            value={icon}
                            onChange={(e) => setIcon(e.target.value)}
                            placeholder="folder"
                        />
                        <p className="text-xs text-muted-foreground">
                            Material icon name (e.g., folder, folder_open, create_new_folder)
                        </p>
                    </div>

                    {/* Color */}
                    <div className="grid gap-2">
                        <Label htmlFor="folder-color">Color</Label>
                        <div className="flex gap-2">
                            <Input
                                id="folder-color"
                                type="color"
                                value={color}
                                onChange={(e) => setColor(e.target.value)}
                                className="h-10 w-20"
                            />
                            <Input
                                value={color}
                                onChange={(e) => setColor(e.target.value)}
                                placeholder="#6366f1"
                                className="flex-1"
                            />
                        </div>
                    </div>

                    {/* Note */}
                    <div className="grid gap-2">
                        <Label htmlFor="folder-note">Note</Label>
                        <Textarea
                            id="folder-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Optional description for this folder"
                            rows={3}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={createCollection.isPending || updateCollection.isPending || !folderKey.trim()}
                    >
                        {createCollection.isPending || updateCollection.isPending
                            ? 'Saving...'
                            : isEdit
                                ? 'Update Folder'
                                : 'Create Folder'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
